import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { User, IdCard, Ban, CheckCircle2, Sparkles, Receipt, ExternalLink } from 'lucide-react';
import { Drawer, Badge, Button, Avatar, Select, Table, TableContainer, EmptyState, Pagination, Spinner } from '@kardume/ui';
import { formatDate, formatDateTime, formatCurrency, formatPhone, backofficeApi } from '@kardume/services';
import type {
  CustomerDto,
  CustomerStatus,
  CustomerGender,
  CustomerPaymentDto,
  PassPlanDto,
  PaymentStatus,
  Tone,
} from '@kardume/types';

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-bottom: 1.25rem;

  .name {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
  }
  .email {
    font-size: 0.85rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Stat = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  padding: 0.85rem 1rem;

  .label {
    font-size: 0.7rem;
    color: ${(p) => p.theme.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .value {
    margin-top: 0.25rem;
    font-size: 1.05rem;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Section = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1.1rem;

  h4 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 0.85rem;
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
  svg {
    color: ${(p) => p.theme.primary};
  }
`;

const SplitRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  margin-bottom: 0.7rem;

  .k {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.textMuted};
  }
  .v {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
`;

const STATUS_LABELS: Record<CustomerStatus, string> = {
  PENDING_ACTIVATION: 'Pendente',
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
};

const GENDER_LABELS: Record<CustomerGender, string> = {
  MALE: 'Masculino',
  FEMALE: 'Feminino',
};

function statusTone(status: CustomerStatus): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_ACTIVATION') return 'warning';
  if (status === 'BLOCKED') return 'danger';
  return 'secondary';
}

const Tabs = styled.div`
  display: flex;
  gap: 1.25rem;
  border-bottom: 1px solid ${(p) => p.theme.border};
  margin-bottom: 1.25rem;
  overflow-x: auto;
`;

const Tab = styled.button<{ $active: boolean }>`
  border: none;
  background: transparent;
  padding: 0.6rem 0.1rem;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  color: ${(p) => (p.$active ? p.theme.primary : p.theme.textMuted)};
  border-bottom: 2px solid ${(p) => (p.$active ? p.theme.primary : 'transparent')};
`;

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem 0;
  justify-content: center;
  color: ${(p) => p.theme.textMuted};
  font-size: 0.875rem;
`;

const ChargeId = styled.span`
  font-family: monospace;
  font-size: 0.75rem;
  color: ${(p) => p.theme.textMuted};
`;

const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  PENDING: 'Aguardando',
  PAID: 'Pago',
  FAILED: 'Recusado',
  CANCELED: 'Cancelado',
  REFUNDED: 'Estornado',
};

const PAYMENT_STATUS_TONES: Record<PaymentStatus, Tone> = {
  PENDING: 'warning',
  PAID: 'success',
  FAILED: 'danger',
  CANCELED: 'secondary',
  REFUNDED: 'info',
};

const PAGE_SIZE = 10;

/**
 * Histórico de compras do cliente.
 *
 * Busca sob demanda (só quando a aba abre) e paginado: é a visão do atendimento,
 * onde a cobrança de meses atrás importa tanto quanto a última. O id da cobrança
 * no provedor fica visível porque é por ele que se acha a transação no painel do
 * Asaas quando o cliente contesta.
 */
const PurchasesTab: React.FC<{ customerId: string }> = ({ customerId }) => {
  const [payments, setPayments] = useState<CustomerPaymentDto[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Trocar de cliente reinicia a paginação - senão a página 3 do anterior
  // apareceria vazia para quem só tem uma compra.
  useEffect(() => {
    setPage(0);
  }, [customerId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    backofficeApi
      .listCustomerPayments(customerId, { page, size: PAGE_SIZE })
      .then((result) => {
        if (!active) return;
        setPayments(result.content);
        setTotalPages(result.totalPages);
        setTotalItems(result.totalElements);
      })
      .catch(() => {
        if (active) setError('Não foi possível carregar as compras deste cliente.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [customerId, page]);

  if (loading) {
    return (
      <Placeholder>
        <Spinner size={18} /> Carregando compras...
      </Placeholder>
    );
  }
  if (error) return <Placeholder>{error}</Placeholder>;
  if (payments.length === 0) {
    return (
      <EmptyState
        icon={<Receipt size={28} />}
        title="Nenhuma compra registrada"
        description="Este cliente ainda não gerou nenhuma cobrança - planos gratuitos não passam pelo pagamento."
      />
    );
  }

  return (
    <>
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Compra</th>
              <th>Forma</th>
              <th>Valor</th>
              <th>Status</th>
              <th>Cobrança</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => (
              <tr key={payment.id}>
                <td>
                  {formatDateTime(payment.createdAt)}
                  {payment.paidAt && (
                    <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>
                      pago em {formatDateTime(payment.paidAt)}
                    </div>
                  )}
                </td>
                <td>
                  {payment.planName ?? payment.description ?? '-'}
                  {payment.failureReason && (
                    <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>{payment.failureReason}</div>
                  )}
                </td>
                <td>
                  {payment.method === 'PIX'
                    ? 'Pix'
                    : `Cartão${payment.cardLast4 ? ` •••• ${payment.cardLast4}` : ''}`}
                </td>
                <td>
                  {formatCurrency(payment.amount)}
                  {payment.netAmount != null && payment.netAmount !== payment.amount && (
                    <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>
                      líquido {formatCurrency(payment.netAmount)}
                    </div>
                  )}
                </td>
                <td>
                  <Badge tone={PAYMENT_STATUS_TONES[payment.status]}>
                    {PAYMENT_STATUS_LABELS[payment.status]}
                  </Badge>
                </td>
                <td>
                  <ChargeId>{payment.providerChargeId}</ChargeId>
                  {payment.invoiceUrl && (
                    <a
                      href={payment.invoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      title="Abrir fatura no provedor"
                      style={{ marginLeft: '0.4rem', display: 'inline-flex', verticalAlign: 'middle' }}
                    >
                      <ExternalLink size={13} />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={PAGE_SIZE}
          onPageChange={(next) => setPage(next - 1)}
        />
      )}
    </>
  );
};

interface CustomerDetailDrawerProps {
  customer: CustomerDto | null;
  isOpen: boolean;
  pending?: boolean;
  /** Planos ativos do Kardume Pass disponíveis para atribuição. */
  plans?: PassPlanDto[];
  /** Indica que uma troca de plano está em andamento (desabilita o controle). */
  planPending?: boolean;
  onClose: () => void;
  onToggleStatus: (customer: CustomerDto) => void;
  onChangePlan?: (customer: CustomerDto, planId: string) => void;
}

const CustomerDetailDrawer: React.FC<CustomerDetailDrawerProps> = ({
  customer,
  isOpen,
  pending,
  plans = [],
  planPending,
  onClose,
  onToggleStatus,
  onChangePlan,
}) => {
  const fullName = customer ? `${customer.firstName} ${customer.lastName}` : '';
  const isBlocked = customer?.status === 'BLOCKED';

  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [tab, setTab] = useState<'geral' | 'compras'>('geral');

  useEffect(() => {
    setSelectedPlanId(customer?.passPlanId ?? '');
  }, [customer?.id, customer?.passPlanId]);

  // Abrir outro cliente (ou reabrir o drawer) sempre começa no Geral.
  useEffect(() => {
    if (isOpen) setTab('geral');
  }, [isOpen, customer?.id]);

  const planOptions = [
    { value: '', label: 'Selecione um plano...' },
    ...plans.map((p) => ({ value: p.id, label: `${p.name} - ${p.price > 0 ? formatCurrency(p.price) : 'Grátis'}` })),
  ];
  const canChangePlan = Boolean(
    onChangePlan && selectedPlanId && selectedPlanId !== customer?.passPlanId
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={customer ? `Cliente - ${fullName}` : 'Cliente'}
      headerActions={
        customer && (
          <>
            {customer.influencer && (
              <Badge tone={customer.influencerActive ? 'primary' : 'secondary'}>
                {customer.influencerActive ? 'Influenciador' : 'Influenciador pausado'}
              </Badge>
            )}
            <Badge tone={statusTone(customer.status)}>{STATUS_LABELS[customer.status]}</Badge>
            <Button
              variant={isBlocked ? 'outline' : 'danger'}
              isLoading={pending}
              onClick={() => onToggleStatus(customer)}
            >
              {isBlocked ? (
                <>
                  <CheckCircle2 size={15} /> Ativar
                </>
              ) : (
                <>
                  <Ban size={15} /> Bloquear
                </>
              )}
            </Button>
          </>
        )
      }
    >
      {customer && (
        <>
          <Tabs>
            <Tab type="button" $active={tab === 'geral'} onClick={() => setTab('geral')}>
              Geral
            </Tab>
            <Tab type="button" $active={tab === 'compras'} onClick={() => setTab('compras')}>
              Compras
            </Tab>
          </Tabs>

          {tab === 'compras' && <PurchasesTab customerId={customer.id} />}
        </>
      )}

      {customer && tab === 'geral' && (
        <>
          <Head>
            <Avatar name={fullName} src={customer.imageUrl ?? undefined} size={52} />
            <div>
              <div className="name">{fullName}</div>
              <div className="email">{customer.email ?? 'Sem e-mail'}</div>
            </div>
          </Head>

          <Stats>
            <Stat>
              <div className="label">Status</div>
              <div className="value">{STATUS_LABELS[customer.status]}</div>
            </Stat>
            <Stat>
              <div className="label">Cadastro</div>
              <div className="value">{formatDate(customer.createdAt)}</div>
            </Stat>
          </Stats>

          <Section>
            <h4>
              <User size={16} /> Dados cadastrais
            </h4>
            <Field>
              <div className="k">Nome</div>
              <div className="v">{fullName}</div>
            </Field>
            <Field>
              <div className="k">E-mail</div>
              <div className="v">{customer.email ?? '-'}</div>
            </Field>
            <Field>
              <div className="k">Telefone</div>
              <div className="v">{formatPhone(customer.phone)}</div>
            </Field>
            <Field>
              <div className="k">Gênero</div>
              <div className="v">{customer.gender ? GENDER_LABELS[customer.gender] : '-'}</div>
            </Field>
            <Field>
              <div className="k">Nascimento</div>
              <div className="v">{customer.birthDate ? formatDate(customer.birthDate) : '-'}</div>
            </Field>
          </Section>

          <SplitRow style={{ marginTop: '1rem' }}>
            <Section>
              <h4>
                <IdCard size={16} /> Documento & identificação
              </h4>
              <Field>
                <div className="k">Documento (CPF)</div>
                <div className="v">{customer.formattedDocument ?? customer.document ?? '-'}</div>
              </Field>
              <Field>
                <div className="k">ID</div>
                <div className="v" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                  {customer.id}
                </div>
              </Field>
            </Section>

            <Section>
              <h4>
                <Sparkles size={16} /> Kardume Pass
              </h4>
              <Field>
                <div className="k">Plano atual</div>
                <div className="v">{customer.passPlanName ?? 'Nenhum plano contratado'}</div>
              </Field>
              {customer.passPlanId && (
                <>
                  <Field>
                    <div className="k">Valor</div>
                    <div className="v">
                      {customer.passPlanPrice && customer.passPlanPrice > 0
                        ? `${formatCurrency(customer.passPlanPrice)}/mês`
                        : 'Grátis'}
                    </div>
                  </Field>
                  <Field>
                    <div className="k">Resgates de cupom</div>
                    <div className="v">
                      {customer.passUnlimitedRedemptions
                        ? 'Ilimitados'
                        : customer.passMonthlyRedemptionLimit
                        ? `${customer.passMonthlyRedemptionLimit}/mês`
                        : '-'}
                    </div>
                  </Field>
                </>
              )}
              {onChangePlan && (
                <div style={{ marginTop: '0.85rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Select
                    options={planOptions}
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(e.target.value)}
                    disabled={planPending}
                  />
                  <Button
                    variant="primary"
                    isLoading={planPending}
                    disabled={!canChangePlan}
                    onClick={() => selectedPlanId && onChangePlan(customer, selectedPlanId)}
                  >
                    Salvar
                  </Button>
                </div>
              )}
            </Section>
          </SplitRow>
        </>
      )}
    </Drawer>
  );
};

export default CustomerDetailDrawer;
